import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";
import { Opportunity } from '@/types/journey';

interface OpportunityFieldProps {
  label: string;
  opportunities: Opportunity[];
  onChange: (opportunities: Opportunity[]) => void;
  placeholder?: string;
}

const OpportunityField: React.FC<OpportunityFieldProps> = ({
  label,
  opportunities,
  onChange,
  placeholder = "Add an opportunity..."
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempOpportunities, setTempOpportunities] = useState<Opportunity[]>(opportunities || []);
  const [newOpportunity, setNewOpportunity] = useState('');

  useEffect(() => {
    setTempOpportunities(opportunities || []);
  }, [opportunities]);

  const handleAddOpportunity = () => {
    if (!newOpportunity.trim()) return;
    
    const newOpportunityItem: Opportunity = {
      id: `opportunity-${Date.now()}`,
      text: newOpportunity.trim()
    };
    
    setTempOpportunities([...tempOpportunities, newOpportunityItem]);
    setNewOpportunity('');
  };

  const handleRemoveOpportunity = (id: string) => {
    setTempOpportunities(tempOpportunities.filter(opportunity => opportunity.id !== id));
  };

  const handleSave = () => {
    onChange(tempOpportunities);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempOpportunities(opportunities || []);
    setIsEditing(false);
    setNewOpportunity('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOpportunity();
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <label className="text-[11px] font-medium block text-muted-foreground">
          {label}
        </label>
        
        <div className="space-y-2">
          {/* Opportunity list */}
          <div className="flex flex-wrap gap-1">
            {tempOpportunities.map((opportunity, index) => (
              <div 
                key={opportunity.id}
                className="group bg-green-50 dark:bg-green-900/20 text-xs px-2 py-1 rounded-full flex items-center gap-1 border border-green-200 dark:border-green-800/30"
              >
                <Lightbulb className="h-3 w-3 text-green-500 dark:text-green-400 mr-0.5" />
                <span className="text-muted-foreground mr-0.5">{index+1}.</span>
                {opportunity.text}
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-muted-foreground hover:text-foreground"
                  onClick={() => handleRemoveOpportunity(opportunity.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Add new opportunity */}
          <div className="flex gap-1">
            <div className="flex-1 relative">
              <Input
                type="text"
                value={newOpportunity}
                onChange={(e) => setNewOpportunity(e.target.value)}
                placeholder={placeholder}
                className="text-xs"
                onKeyDown={handleKeyDown}
              />
              {newOpportunity && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-5 w-5 p-0"
                  onClick={() => setNewOpportunity('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={handleAddOpportunity}
              disabled={!newOpportunity.trim()}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          
          <div className="flex justify-end space-x-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-6 text-xs"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium block text-muted-foreground">
        {label}
      </label>
      
      {tempOpportunities.length > 0 ? (
        <div 
          className="flex flex-wrap gap-1 min-h-[2.5rem] cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 p-1 rounded"
          onClick={() => setIsEditing(true)}
        >
          {tempOpportunities.map((opportunity, index) => (
            <div 
              key={opportunity.id}
              className="bg-green-50 dark:bg-green-900/20 text-xs px-2 py-1 rounded-full flex items-center gap-1 border border-green-200 dark:border-green-800/30"
            >
              <Lightbulb className="h-3 w-3 text-green-500 dark:text-green-400 mr-0.5" />
              <span className="text-muted-foreground mr-0.5">{index+1}.</span>
              {opportunity.text}
            </div>
          ))}
        </div>
      ) : (
        <div 
          className="text-xs min-h-[2.5rem] cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 p-1 rounded text-muted-foreground italic"
          onClick={() => setIsEditing(true)}
        >
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default OpportunityField; 